import { useState } from "preact/hooks";

import { api } from "@/api/client";
import type { ClaudeCacheSavings, CostResponse, DayCost, ModeTokenStats } from "@/api/types";
import { InfoDot, PanelHelp } from "@/components/PanelHelp";
import { Staleness } from "@/components/Staleness";
import { useECharts } from "@/hooks/useECharts";
import { usePoll } from "@/hooks/usePoll";
import { t, type TranslationKey } from "@/i18n";
import { downloadTenantCostCsv } from "@/lib/costExport";
import { tenantCostRows, usdPerSession, usdPerTurn } from "@/lib/costRows";
import {
  formatDecimal,
  formatInt,
  formatRatio,
  formatUsd,
  modeLabel,
  tenantLabel,
} from "@/lib/format";
import { costWindow, includeTestSessions, selectedTenant } from "@/state/filters";

const DAY_MS = 86_400_000;

const SERIES_KEYS = ["input_usd", "output_usd", "cache_read_usd", "cache_create_usd"] as const;
const SERIES_LABEL_KEYS: Record<(typeof SERIES_KEYS)[number], TranslationKey> = {
  input_usd: "cost.series.input",
  output_usd: "cost.series.output",
  cache_read_usd: "cost.series.cache_read",
  cache_create_usd: "cost.series.cache_create",
};
const SERIES_COLORS = ["#002da5", "#fab400", "#e6d2b4", "#16213e"];

type ChartMode = "daily" | "area" | "cumulative";

const CHART_MODES: Array<{ id: ChartMode; label: TranslationKey }> = [
  { id: "daily", label: "cost.chart.daily" },
  { id: "area", label: "cost.chart.area" },
  { id: "cumulative", label: "cost.chart.cumulative" },
];

/** Expand a sparse daily series so every date between the first and last is
 * present, absent days as explicit zeros.
 *
 * A bar chart can omit an idle day and stay readable. An area chart cannot:
 * it interpolates between neighbouring points, so a gap renders as a slope
 * across the missing days instead of a return to zero. With nine active days
 * in a thirty-day window that is the difference between "we tested on nine
 * days" and "we spent money continuously".
 */
function withIdleDays<T extends { date: string }>(rows: readonly T[]): T[] {
  if (rows.length === 0) return [];
  const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
  const byDate = new Map(sorted.map((r) => [r.date, r]));
  // Zero row derived from the real shape, so new numeric fields fill in too.
  const zero = Object.fromEntries(
    Object.keys(sorted[0]).map((k) => [k, k === "date" ? "" : 0]),
  ) as T;

  const out: T[] = [];
  const last = Date.parse(`${sorted[sorted.length - 1].date}T00:00:00Z`);
  for (let ts = Date.parse(`${sorted[0].date}T00:00:00Z`); ts <= last; ts += DAY_MS) {
    const date = new Date(ts).toISOString().slice(0, 10);
    out.push(byDate.get(date) ?? ({ ...zero, date } as T));
  }
  return out;
}

/** ECharts option for the three chart shapes.
 *
 * `daily` is the default: a stacked bar per day, which is honest about an idle
 * day being zero rather than a point on a slope. `area` is the same four-way
 * split as a continuous band. `cumulative` drops the split and plots the
 * running total, which is the shape that answers "what has this window cost
 * so far" at a glance.
 */
function buildChartOption(daily: readonly DayCost[], mode: ChartMode) {
  const dates = daily.map((d) => d.date);
  const sparse = daily.length < 3;
  const base = {
    tooltip: { trigger: "axis" as const },
    // Bars sit inside the band; a line/area has to reach the axis edges.
    xAxis: { type: "category" as const, boundaryGap: mode === "daily", data: dates },
    yAxis: { type: "value" as const, min: 0 },
  };

  if (mode === "cumulative") {
    let running = 0;
    const cumulative = daily.map((d) => (running += d.total_usd));
    return {
      ...base,
      title: {
        text: t("cost.chart.cumulativeTitle"),
        left: "center",
        textStyle: { fontSize: 14 },
      },
      series: [
        {
          name: t("cost.series.cumulative"),
          type: "line",
          smooth: false,
          showSymbol: sparse,
          lineStyle: { width: 2 },
          areaStyle: { opacity: 0.15 },
          data: cumulative,
          itemStyle: { color: SERIES_COLORS[0] },
        },
      ],
    };
  }

  return {
    ...base,
    title: { text: t("cost.chart.dailyTitle"), left: "center", textStyle: { fontSize: 14 } },
    legend: { bottom: 0 },
    series: SERIES_KEYS.map((k, i) => ({
      name: t(SERIES_LABEL_KEYS[k]),
      type: mode === "area" ? "line" : "bar",
      stack: "all",
      ...(mode === "area"
        ? {
            // Straight segments, not a spline: a spline overshoots on spiky
            // sparse data and would draw cost below zero.
            smooth: false,
            // One or two points have no segment to fill — without symbols the
            // series would render as nothing at all (the 24h window).
            showSymbol: sparse,
            lineStyle: { width: 0 },
            areaStyle: { opacity: 1 },
            emphasis: { focus: "series" as const },
          }
        : {}),
      data: daily.map((d) => (d as unknown as Record<string, number>)[k]),
      itemStyle: { color: SERIES_COLORS[i] },
    })),
  };
}

/** Totals the response does not carry, derived from the per-mode rows. */
function deriveTotals(perMode: Record<string, ModeTokenStats>) {
  const rows = Object.values(perMode);
  const sum = (pick: (m: ModeTokenStats) => number) =>
    rows.reduce((acc, m) => acc + pick(m), 0);

  const turns = sum((m) => m.turns);
  const sessions = sum((m) => m.sessions);
  const usd = sum((m) => m.total_usd);
  return {
    turns,
    sessions,
    usd,
    usdPerTurn: turns > 0 ? usd / turns : 0,
    usdPerSession: sessions > 0 ? usd / sessions : 0,
  };
}

/** The Claude prompt cache's steering numbers, all from the backend, which
 * prices every write at its own TTL. The read/write ratio says how often a
 * written prefix is read back; below the break-even (about 1,1 at the 1-hour
 * TTL, 0,28 at 5 minutes) the cache costs more than it saves. A backend that
 * sends no write figures leaves them unknown (null), never zero. */
function cacheEconomics(c: ClaudeCacheSavings) {
  const writes = c.cache_write_tokens ?? null;
  const premiumUsd = c.write_premium_usd ?? null;
  const ratio = writes ? c.cache_read_tokens / writes : null;
  const breakEven = c.break_even_read_write_ratio ?? null;
  return {
    reads: c.cache_read_tokens,
    writes,
    ratio,
    breakEven,
    belowBreakEven: ratio !== null && breakEven !== null && ratio < breakEven,
    premiumUsd,
    netUsd: premiumUsd === null ? null : c.savings_usd - premiumUsd,
  };
}

/** What a turns read that reached the read limit leaves out; undefined while
 * the read is whole or the backend predates the flags. */
function readLimitWarning(
  data: CostResponse | undefined,
  read: "complete" | "last_7d_complete" | "last_30d_complete",
  key: TranslationKey,
  params: Record<string, string | number> = {},
): string | undefined {
  if (!data || data[read] !== false || data.read_limit === undefined) return undefined;
  return t(key, { ...params, n: formatInt(data.read_limit) });
}

/** Model plus its input rate, the second line of a mode cell. */
function ModelCell({ s }: { s: ModeTokenStats }) {
  return (
    <>
      {s.model_label || s.model || "—"}
      {s.input_usd_per_1m ? (
        <span class="muted" style="display: block; font-size: 0.85em;">
          {t("cost.ratePer1m", { rate: formatDecimal(s.input_usd_per_1m) })}
        </span>
      ) : null}
    </>
  );
}

export function CostView() {
  const { data, loading, error, lastFetched, refetch } = usePoll(
    (s) =>
      api.cost(
        {
          window: costWindow.value,
          includeTest: includeTestSessions.value,
          tenant: selectedTenant.value,
        },
        s,
      ),
    [costWindow.value, includeTestSessions.value, selectedTenant.value],
  );
  const [chartMode, setChartMode] = useState<ChartMode>("daily");

  // Partners by spend, each followed by its modes by spend — the mode row is
  // the grain an invoice line is written at.
  const tenantRows = tenantCostRows(data?.per_tenant ?? {}, data?.per_tenant_mode ?? {});

  const daily = data ? withIdleDays(data.daily) : [];
  const dailyOption = data ? buildChartOption(daily, chartMode) : null;
  const dailyRef = useECharts(dailyOption);
  const totals = data ? deriveTotals(data.per_mode) : null;
  const cache = data ? cacheEconomics(data.claude_cache_savings) : null;
  const windowCut = readLimitWarning(data, "complete", "cost.incomplete");

  return (
    <div style="display: grid; gap: 24px;">
      <div style="display: flex; align-items: center; gap: 24px;">
        <Staleness
          lastFetched={lastFetched}
          loading={loading}
          error={error}
          onRefresh={refetch}
        />
      </div>
      {windowCut && <div class="kpi-sub limit-warn">{windowCut}</div>}

      <section class="card" style="padding-bottom: var(--space-3);">
        <PanelHelp k="cost.overview" />
        <div class="grid grid-3">
          <KpiCard
            label={t("cost.todaySoFar")}
            value={data ? formatUsd(data.today_so_far_usd) : "—"}
            sub={data ? t("cost.projectedEod", { v: formatUsd(data.today_projected_usd) }) : ""}
          />
          <KpiCard
            label={t("cost.last7d")}
            value={data ? formatUsd(data.last_7d_usd) : "—"}
            sub={
              data ? t("cost.monthlyProjection", { v: formatUsd(data.monthly_projection_usd) }) : ""
            }
            warn={readLimitWarning(data, "last_7d_complete", "cost.spanIncomplete", { days: 7 })}
          />
          <KpiCard
            label={t("cost.annualized")}
            value={data ? formatUsd(data.annual_projection_usd) : "—"}
            sub={t("cost.annualizedSub")}
            warn={readLimitWarning(data, "last_30d_complete", "cost.spanIncomplete", { days: 30 })}
          />
        </div>
      </section>

      <section class="card">
        <h2>{t("cost.efficiency")}</h2>
        <PanelHelp k="cost.efficiency" />
        {totals && cache && (
          <div class="grid grid-3">
            <div>
              <h3>{t("cost.perTurn")}</h3>
              <div class="kpi">{formatUsd(totals.usdPerTurn)}</div>
              <div class="kpi-sub">
                {t("cost.perTurnSub", {
                  turns: formatInt(totals.turns),
                  perSession: formatUsd(totals.usdPerSession),
                  sessions: formatInt(totals.sessions),
                })}
              </div>
            </div>
            <div>
              <h3>{t("cost.cacheRw")}</h3>
              <div class={`kpi ${cache.belowBreakEven ? "hl-orange" : ""}`}>
                {cache.ratio === null ? "—" : formatRatio(cache.ratio)}
              </div>
              <div class="kpi-sub">
                {t("cost.cacheRwSub", {
                  read: formatInt(cache.reads),
                  write: cache.writes === null ? "—" : formatInt(cache.writes),
                  breakEven: cache.breakEven === null ? "—" : formatRatio(cache.breakEven),
                })}
              </div>
            </div>
            <div>
              <h3>{t("cost.windowTotal")}</h3>
              <div class="kpi">{formatUsd(totals.usd)}</div>
              <div class="kpi-sub">{t("cost.windowTotalSub")}</div>
            </div>
          </div>
        )}
      </section>

      <section class="card">
        <h2>
          {t("cost.cacheSavings")} <InfoDot k="cacheHit" />
        </h2>
        <PanelHelp k="cost.cacheSavings" />
        {data && cache && (
          <div class="grid grid-3">
            <div>
              <h3>{t("cost.cacheReads")}</h3>
              <div class="kpi">
                {formatInt(data.claude_cache_savings.cache_read_tokens)}
              </div>
              <div class="kpi-sub">{t("cost.cacheReadsSub")}</div>
            </div>
            <div>
              <h3>{t("cost.savedOnReads")}</h3>
              <div class="kpi hl-orange">
                {formatUsd(data.claude_cache_savings.savings_usd)}
              </div>
              <div class="kpi-sub">
                {t("cost.savedOnReadsSub", {
                  naive: formatUsd(data.claude_cache_savings.naive_usd_without_cache),
                })}
              </div>
            </div>
            <div>
              <h3>{t("cost.netSaving")}</h3>
              <div class="kpi hl-orange">
                {cache.netUsd === null ? "—" : formatUsd(cache.netUsd)}
              </div>
              <div class="kpi-sub">
                {t("cost.netSavingSub", {
                  premium: cache.premiumUsd === null ? "—" : formatUsd(cache.premiumUsd),
                  actual: formatUsd(data.claude_cache_savings.actual_usd_with_cache),
                })}
              </div>
            </div>
          </div>
        )}
      </section>

      <section class="card">
        <PanelHelp k="cost.daily" />
        <div class="toolbar" style="margin-bottom: var(--space-3);">
          {CHART_MODES.map((m) => (
            <button
              key={m.id}
              class={`window-pill ${chartMode === m.id ? "active" : ""}`}
              onClick={() => setChartMode(m.id)}
            >
              {t(m.label)}
            </button>
          ))}
        </div>
        <div ref={dailyRef} class="chart chart-tall" />
      </section>

      {tenantRows.length > 0 && data && (
        <section class="card">
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;">
            <h2 style="margin: 0;">{t("cost.perTenant")}</h2>
            <button
              class="window-pill"
              title={t("cost.exportCsvTitle")}
              onClick={() =>
                downloadTenantCostCsv(tenantRows, {
                  windowLabel: data.window_label,
                  tenantFilter: data.tenant ?? "",
                  generatedAt: data.timestamp,
                })
              }
            >
              {t("cost.exportCsv")}
            </button>
          </div>
          <PanelHelp k="cost.perTenant" />
          <div class="kpi-sub" style="margin-bottom: var(--space-3);">
            {t("cost.perTenantModeHint")}
          </div>
          <table class="table">
            <thead>
              <tr>
                <th>{t("cost.col.partner")}</th>
                <th>{t("cost.col.modeModel")}</th>
                <th>{t("cost.col.turns")}</th>
                <th>{t("cost.col.sessions")}</th>
                <th>{t("cost.col.inputTok")}</th>
                <th>{t("cost.col.outputTok")}</th>
                <th>{t("cost.col.cost")}</th>
                <th>{t("cost.col.perTurn")}</th>
                <th>{t("cost.col.perSession")}</th>
              </tr>
            </thead>
            <tbody>
              {tenantRows.map((row) => (
                <tr
                  key={row.kind === "tenant" ? row.tenant : `${row.tenant}:${row.mode}`}
                  class={row.kind === "tenant" ? "row-group" : "row-member"}
                >
                  <td>{row.kind === "tenant" ? tenantLabel(row.tenant) : ""}</td>
                  <td>
                    {row.kind === "tenant" ? (
                      t("common.total")
                    ) : (
                      <>
                        {modeLabel(row.mode ?? "")}
                        <span class="muted" style="display: block; font-size: 0.85em;">
                          <ModelCell s={row.stats} />
                        </span>
                      </>
                    )}
                  </td>
                  <td>{formatInt(row.stats.turns)}</td>
                  <td>{formatInt(row.stats.sessions)}</td>
                  <td>{formatInt(row.stats.input_tokens)}</td>
                  <td>{formatInt(row.stats.output_tokens)}</td>
                  <td>{formatUsd(row.stats.total_usd)}</td>
                  <td>{formatUsd(usdPerTurn(row.stats))}</td>
                  <td>{formatUsd(usdPerSession(row.stats))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(data.unattributed_turns ?? 0) > 0 && (
            <div class="kpi-sub">
              {t("cost.unattributed", { n: formatInt(data.unattributed_turns ?? 0) })}
            </div>
          )}
        </section>
      )}

      <section class="card">
        <h2>{t("cost.perMode")}</h2>
        <PanelHelp k="cost.perMode" />
        <table class="table">
          <thead>
            <tr>
              <th>{t("common.mode")}</th>
              <th>{t("common.model")}</th>
              <th>{t("cost.col.turns")}</th>
              <th>{t("cost.col.inputTok")}</th>
              <th>{t("cost.col.outputTok")}</th>
              <th>{t("cost.col.cacheRead")}</th>
              <th>{t("cost.col.cacheCreate")}</th>
              <th>{t("cost.col.cost")}</th>
              <th>{t("cost.col.avgTokSession")}</th>
            </tr>
          </thead>
          <tbody>
            {data &&
              Object.entries(data.per_mode).map(([mode, s]) => (
                <tr key={mode}>
                  <td>{modeLabel(mode)}</td>
                  <td>
                    <ModelCell s={s} />
                  </td>
                  <td>{formatInt(s.turns)}</td>
                  <td>{formatInt(s.input_tokens)}</td>
                  <td>{formatInt(s.output_tokens)}</td>
                  <td>{formatInt(s.cache_read_tokens)}</td>
                  <td>{formatInt(s.cache_create_tokens)}</td>
                  <td>{formatUsd(s.total_usd)}</td>
                  <td>{formatInt(Math.round(s.avg_tokens_per_session))}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
  warn,
}: {
  label: string;
  value: string;
  sub?: string;
  warn?: string;
}) {
  return (
    <div class="card" style="border-left: 4px solid var(--brand-blue);">
      <h3>{label}</h3>
      <div class="kpi">{value}</div>
      {sub && <div class="kpi-sub">{sub}</div>}
      {warn && <div class="kpi-sub limit-warn">{warn}</div>}
    </div>
  );
}
